export const API_ENDPOINTS = {
    // User endpoints (CRM/Admin specific)
    USERS: {
        LOGIN: '/users/login',
        REGISTER: '/users/register',
        LOGOUT: '/users/logout',
        PROFILE: '/users/profile',
        PERMISSIONS: '/users/permissions',
        FORGOT_PASSWORD: '/users/forgot-password',
        RESET_PASSWORD: '/users/reset-password',
        VERIFY_EMAIL: '/users/verify-email',
        RESEND_VERIFICATION: '/users/resend-verification',
    },

    // Customer Auth endpoints (Traders)
    CUSTOMERS: {
        LOGIN: '/customers/login',
        REGISTER: '/customers/register',
        LOGOUT: '/customers/logout',
        PROFILE: '/customers/profile',
        CHANGE_PASSWORD: '/customers/change-password',
        FORGOT_PASSWORD: '/customers/forgot-password',
        RESET_PASSWORD: '/customers/reset-password',
    },

    // Trader endpoints - Updated for forex architecture
    TRADER: {
        // Enhanced Assets
        ASSETS: '/trader/assets',
        ASSETS_TYPES: '/trader/assets/types',
        ASSETS_FOREX_PAIRS: '/trader/assets/forex-pairs',
        ASSETS_CRYPTO_PAIRS: '/trader/assets/crypto-pairs',
        ASSET_DETAILS: (assetId: string) => `/trader/assets/${assetId}`,

        // Trading Instruments (replaces trading-pairs)
        INSTRUMENTS: '/trader/instruments',
        INSTRUMENTS_POPULAR: '/trader/instruments/popular',
        INSTRUMENT_DETAILS: (instrumentId: string) => `/trader/instruments/${instrumentId}`,
        INSTRUMENT_CALCULATE: (instrumentId: string) =>
            `/trader/instruments/${instrumentId}/calculate`,
        INSTRUMENT_MARKET_STATUS: (instrumentId: string) =>
            `/trader/instruments/${instrumentId}/market-status`,
        INSTRUMENT_ORDER_BOOK: (instrumentId: string) =>
            `/trader/instruments/${instrumentId}/order-book`,
        INSTRUMENT_ORDER_BOOK_STREAM: (instrumentId: string) =>
            `/trader/instruments/${instrumentId}/order-book/stream`,

        // Chart Data (TradingView UDF)
        CHART_CONFIG: '/trader/chart/config',
        CHART_SYMBOLS: '/trader/chart/symbols',
        CHART_SEARCH: '/trader/chart/search',
        CHART_HISTORY: '/trader/chart/history',

        // Price data
        PRICES: '/trader/prices',
        PRICES_LATEST: '/trader/prices/latest',
        PRICES_HISTORY: '/trader/prices/history',
        PRICE_DETAILS: (tradingPair: string) => `/trader/prices/${tradingPair}`,

        // Enhanced Balance Management
        BALANCE: {
            ME: '/trader/balance/me',
            DETAILS: '/trader/balance/details',
            HISTORY: '/trader/balance/history',
            PNL: '/trader/balance/pnl',
            MARGIN: '/trader/balance/margin',
            WALLETS: '/trader/balance/wallets',
            TRADING_ACCOUNTS: '/trader/balance/trading-accounts',
        },

        // Enhanced Order Management
        ORDERS: {
            LIST: '/trader/trades',
            CREATE: '/trader/trades',
            HISTORY: '/trader/trades',
            SUMMARY: '/trader/trades/summary',
            CALCULATE: (instrumentId: string) => `/trader/instruments/${instrumentId}/calculate`,
            DETAILS: (tradeId: string) => `/trader/trades/${tradeId}`,
            UPDATE: (tradeId: string) => `/trader/trades/${tradeId}`,
            CLOSE: (tradeId: string) => `/trader/trades/${tradeId}/close`,
            CANCEL: (tradeId: string) => `/trader/trades/${tradeId}/cancel`,
        },

        // Position Management (new for forex) - now using unified trades
        POSITIONS: {
            LIST: '/trader/trades?status=open',
            SUMMARY: '/trader/trades/summary',
            DETAILS: (positionId: string) => `/trader/trades/${positionId}`,
            CLOSE: (positionId: string) => `/trader/trades/${positionId}/close`,
            UPDATE_STOP_LOSS: (positionId: string) => `/trader/trades/${positionId}`,
            UPDATE_TAKE_PROFIT: (positionId: string) => `/trader/trades/${positionId}`,
        },

        // Trade Management
        TRADES: {
            LIST: '/trader/trades',
            HISTORY: '/trader/trades/history',
            SUMMARY: '/trader/trades/summary',
            DETAILS: (tradeId: string) => `/trader/trades/${tradeId}`,
            STREAM: '/trader/trades/stream',
        },

        // Enhanced Wallet Management
        WALLETS: {
            LIST: '/trader/wallets',
            SUMMARY: '/trader/wallets/summary',
            DETAILS: (walletId: string) => `/trader/wallets/${walletId}`,
            DEPOSIT: '/trader/wallets/deposit',
            WITHDRAW: '/trader/wallets/withdraw',
            TRANSFER: '/trader/wallets/transfer',
            CONVERT: '/trader/wallets/convert',
        },

        // Transaction Management
        TRANSACTIONS: {
            LIST: '/trader/transactions',
            CREATE: '/trader/transactions',
            DETAILS: (transactionId: string) => `/trader/transactions/${transactionId}`,
            CANCEL: (transactionId: string) => `/trader/transactions/${transactionId}/cancel`,
            PAYMENT_METHODS: '/trader/transactions/payment-methods',
            TRADING_ACCOUNT_DEPOSIT: '/trader/transactions/trading-account/deposit',
            TRADING_ACCOUNT_WITHDRAWAL: '/trader/transactions/trading-account/withdrawal',
        },

        // Account Management
        ACCOUNT: {
            PROFILE: '/trader/account/profile',
            UPDATE_PROFILE: '/trader/account/profile',
            TRADING_SETTINGS: '/trader/account/trading-settings',
            UPDATE_TRADING_SETTINGS: '/trader/account/trading-settings',
            RISK_SETTINGS: '/trader/account/risk-settings',
            UPDATE_RISK_SETTINGS: '/trader/account/risk-settings',
        },

        // KYC Management
        KYC: {
            LIST: '/trader/kyc',
            CREATE: '/trader/kyc',
            DETAILS: (kycId: string) => `/trader/kyc/${kycId}`,
        },

        // Risk Management
        RISK: {
            LIMITS: '/trader/risk/limits',
            EXPOSURE: '/trader/risk/exposure',
            MARGIN_REQUIREMENTS: '/trader/risk/margin-requirements',
        },

        // Market Analysis
        MARKET: {
            OVERVIEW: '/trader/market/overview',
            SENTIMENT: '/trader/market/sentiment',
            ECONOMIC_CALENDAR: '/trader/market/economic-calendar',
            NEWS: '/trader/market/news',
        },

        // Trading Tools
        TOOLS: {
            PIP_CALCULATOR: '/trader/tools/pip-calculator',
            MARGIN_CALCULATOR: '/trader/tools/margin-calculator',
            PROFIT_CALCULATOR: '/trader/tools/profit-calculator',
            CURRENCY_CONVERTER: '/trader/tools/currency-converter',
        },

        // Notifications
        NOTIFICATIONS: {
            LIST: '/trader/notifications',
            PRICE_ALERTS: '/trader/notifications/price-alerts',
            DELETE_PRICE_ALERT: (alertId: string) =>
                `/trader/notifications/price-alerts/${alertId}`,
            SETTINGS: '/trader/notifications/settings',
        },

        // Reports
        REPORTS: {
            TRADING_SUMMARY: '/trader/reports/trading-summary',
            PERFORMANCE: '/trader/reports/performance',
            TAX_REPORT: '/trader/reports/tax-report',
            STATEMENT: '/trader/reports/statement',
        },

        // Impersonation
        IMPERSONATION: {
            STATUS: '/trader/impersonation/status',
            STOP: '/trader/impersonation/stop',
        },
    },

    // CRM endpoints
    CRM: {
        USERS: '/crm/users',
        ROLES: '/crm/roles',
        VIEWS: '/crm/views',
        VIEWS_REORDER: '/crm/views/reorder',
        VIEW: (viewId: string) => `/crm/views/${viewId}`,
        LEAD_STATUSES: '/crm/lead-statuses',
        LEADS: '/crm/leads',
        DUPLICATES: '/crm/duplicates',
        KYC: '/crm/kyc-requests',
        KYC_CREATE_AND_APPROVE: '/crm/kyc-requests/create-and-approve',
        KYC_DOCUMENTS: {
            VIEW: (kycRequestId: string, documentId: string) =>
                `/crm/kyc-requests/${kycRequestId}/documents/${documentId}/view`,
            DOWNLOAD: (kycRequestId: string, documentId: string) =>
                `/crm/kyc-requests/${kycRequestId}/documents/${documentId}/download`,
        },
        NOTES: {
            LIST: (entityType: string, entityId: string) => `/crm/${entityType}/${entityId}/notes`,
            CREATE: (entityType: string, entityId: string) =>
                `/crm/${entityType}/${entityId}/notes`,
            DETAILS: (entityType: string, entityId: string, noteId: string) =>
                `/crm/${entityType}/${entityId}/notes/${noteId}`,
            UPDATE: (entityType: string, entityId: string, noteId: string) =>
                `/crm/${entityType}/${entityId}/notes/${noteId}`,
            DELETE: (entityType: string, entityId: string, noteId: string) =>
                `/crm/${entityType}/${entityId}/notes/${noteId}`,
            DASHBOARD: '/crm/notes/dashboard',
        },
        USER_NOTES: {
            LIST: (userId: string) => `/crm/users/${userId}/notes`,
            BULK_DELETE: (userId: string) => `/crm/users/${userId}/notes/bulk`,
            DELETE_ALL: (userId: string) => `/crm/users/${userId}/notes/all`,
        },
        CUSTOMERS: {
            LIST: '/crm/customers',
            DETAILS: (customerId: string) => `/crm/customers/${customerId}`,
            ADDRESSES: (customerId: string) => `/crm/customers/${customerId}/addresses`,
            ADDRESS: (customerId: string, addressId: string) =>
                `/crm/customers/${customerId}/addresses/${addressId}`,
            ACCOUNTS: (customerId: string) => `/crm/customers/${customerId}/accounts`,
            TRANSACTIONS: (customerId: string) => `/crm/customers/${customerId}/transactions`,
            DOCUMENTS: (customerId: string) => `/crm/customers/${customerId}/documents`,
            DOCUMENT: (customerId: string, documentId: string) =>
                `/crm/customers/${customerId}/documents/${documentId}`,
            RESET_PASSWORD: (customerId: string) => `/crm/customers/${customerId}/reset-password`,
            BULK_DELETE: '/crm/customers/bulk-delete',
            BULK_ASSIGN: '/crm/customers/bulk-assign',
            BULK_UPDATE_STATUS: '/crm/customers/bulk-update-status',
        },

        // Customer Documents
        DOCUMENTS: {
            LIST: (customerId: string) => `/crm/customers/${customerId}/documents`,
            UPLOAD: (customerId: string) => `/crm/customers/${customerId}/documents`,
            DETAILS: (customerId: string, documentId: string) =>
                `/crm/customers/${customerId}/documents/${documentId}`,
            DELETE: (customerId: string, documentId: string) =>
                `/crm/customers/${customerId}/documents/${documentId}`,
            VIEW: (customerId: string, documentId: string) =>
                `/crm/customers/${customerId}/documents/${documentId}/view`,
            DOWNLOAD: (customerId: string, documentId: string) =>
                `/crm/customers/${customerId}/documents/${documentId}/download`,
            UPDATE_STATUS: (customerId: string, documentId: string) =>
                `/crm/customers/${customerId}/documents/${documentId}/status`,
        },

        // CRM Orders Management (for CRM users creating trades for customers)
        ORDERS: {
            LIST: '/crm/trades',
            CREATE: '/crm/trades',
            HISTORY: '/crm/trades',
            SUMMARY: '/crm/trades/summary',
            DETAILS: (tradeId: string) => `/crm/trades/${tradeId}`,
            UPDATE: (tradeId: string) => `/crm/trades/${tradeId}`,
            CLOSE: (tradeId: string) => `/crm/trades/${tradeId}/close`,
            PARTIAL_CLOSE: (tradeId: string) => `/crm/trades/${tradeId}/partial-close`,
            CANCEL: (tradeId: string) => `/crm/trades/${tradeId}/cancel`,
            DELETE: (tradeId: string) => `/crm/trades/${tradeId}`,
            BULK_CANCEL: '/crm/trades/bulk-cancel',
            BULK_MODIFY: '/crm/trades/bulk-modify',
        },

        // CRM Trade History Management
        TRADE_HISTORY: {
            LIST: '/crm/trade-history',
            DETAILS: (tradeHistoryId: string) => `/crm/trade-history/${tradeHistoryId}`,
            UPDATE: (tradeHistoryId: string) => `/crm/trade-history/${tradeHistoryId}`,
            DELETE: (tradeHistoryId: string) => `/crm/trade-history/${tradeHistoryId}`,
            REOPEN: (tradeHistoryId: string) => `/crm/trade-history/${tradeHistoryId}/reopen`,
            BULK_REOPEN: '/crm/trade-history/bulk-reopen',
            STATISTICS: '/crm/trade-history/statistics',
            EXPORT: '/crm/trade-history/export',
            ANALYTICS: '/crm/trade-history/analytics/overview',
            CUSTOMER_SUMMARY: (customerId: string) =>
                `/crm/trade-history/customer/${customerId}/summary`,
            INSTRUMENT_PERFORMANCE: (instrumentId: string) =>
                `/crm/trade-history/instrument/${instrumentId}/performance`,
        },

        // CRM Trade Management (using unified trades endpoints)
        TRADES: {
            LIST: '/crm/trades',
            DETAILS: (tradeId: string) => `/crm/trades/${tradeId}`,
            UPDATE: (tradeId: string) => `/crm/trades/${tradeId}`,
            DELETE: (tradeId: string) => `/crm/trades/${tradeId}`,
            SUMMARY: '/crm/trades/statistics',
            EXPORT: '/crm/trades/export',
            ANALYTICS: '/crm/trades/analytics/overview',
            CUSTOMER_SUMMARY: (customerId: string) => `/crm/trades/customer/${customerId}/summary`,
            INSTRUMENT_PERFORMANCE: (instrumentId: string) =>
                `/crm/trades/instrument/${instrumentId}/performance`,
        },

        // CRM Position Management - now using unified trades
        POSITIONS: {
            LIST: '/crm/trades?status=open',
            DETAILS: (positionId: string) => `/crm/trades/${positionId}`,
            UPDATE_STOP_LOSS: (positionId: string) => `/crm/trades/${positionId}`,
            UPDATE_TAKE_PROFIT: (positionId: string) => `/crm/trades/${positionId}`,
            CLOSE: (positionId: string) => `/crm/trades/${positionId}/close`,
            BULK_CLOSE: '/crm/trades/bulk-close',
        },

        // CRM Transactions Management
        TRANSACTIONS: {
            LIST: '/crm/transactions',
            CREATE: '/crm/transactions',
            DETAILS: (transactionId: string) => `/crm/transactions/${transactionId}`,
            UPDATE: (transactionId: string) => `/crm/transactions/${transactionId}`,
            DELETE: (transactionId: string) => `/crm/transactions/${transactionId}`,
        },

        // Impersonation
        IMPERSONATION: {
            STATUS: '/crm/impersonation/status',
            START: '/crm/impersonation/start',
            STOP: '/crm/impersonation/stop',
            LIST_CUSTOMERS: '/crm/impersonation/customers',
        },
        TASKS: '/crm/tasks',
        INVOICES: '/crm/invoices',
        LOGS: '/crm/logs',
        LOGS_EXPORT: '/crm/logs/export',
        INSTRUMENTS: '/crm/instruments',
        INSTRUMENTS_CALCULATE: (instrumentId: string) =>
            `/crm/instruments/${instrumentId}/calculate`,
        ASSETS: '/crm/assets',
        SYSTEM_CONFIGURATION: '/crm/system-configuration',
        DASHBOARD: '/crm/dashboard',
        CHARTS: {
            CURRENT_PRICES: '/crm/charts/current-prices',
        },

        // Professional CRM Features
        IB_PARTNERS: '/crm/ib-partners',
        COMMISSION_STRUCTURES: '/crm/commission-structures',
        COMMISSIONS: '/crm/commissions',
        CAMPAIGNS: {
            LIST: '/crm/campaigns',
            CREATE: '/crm/campaigns',
            DETAILS: (campaignId: string) => `/crm/campaigns/${campaignId}`,
            UPDATE: (campaignId: string) => `/crm/campaigns/${campaignId}`,
            DELETE: (campaignId: string) => `/crm/campaigns/${campaignId}`,
            FIELD_CATALOG: '/crm/campaigns/field-catalog',
            SOURCE_FIELDS: (campaignId: string) => `/crm/campaigns/${campaignId}/source-fields`,
            SUGGEST_MAPPING: (campaignId: string) => `/crm/campaigns/${campaignId}/suggest-mapping`,
            MAPPING: (campaignId: string) => `/crm/campaigns/${campaignId}/mapping`,
            PREVIEW: (campaignId: string) => `/crm/campaigns/${campaignId}/preview`,
            ROTATE_TOKEN: (campaignId: string) => `/crm/campaigns/${campaignId}/rotate-token`,
            SUBMISSIONS: (campaignId: string) => `/crm/campaigns/${campaignId}/submissions`,
            SUBMISSION_DETAILS: (campaignId: string, submissionId: string) =>
                `/crm/campaigns/${campaignId}/submissions/${submissionId}`,
            SUBMISSION_REPROCESS: (campaignId: string, submissionId: string) =>
                `/crm/campaigns/${campaignId}/submissions/${submissionId}/reprocess`,
            SUBMISSIONS_REPROCESS_FAILED: (campaignId: string) =>
                `/crm/campaigns/${campaignId}/submissions/reprocess-failed`,
            FACEBOOK_STATUS: '/crm/campaigns/facebook/status',
            FACEBOOK_CONNECT: '/crm/campaigns/facebook/connect',
            FACEBOOK_DISCONNECT: '/crm/campaigns/facebook/connection',
            FACEBOOK_PAGE_FORMS: (pageId: string) =>
                `/crm/campaigns/facebook/pages/${pageId}/forms`,
            FACEBOOK_LINK_FORM: (campaignId: string) =>
                `/crm/campaigns/${campaignId}/facebook-form`,
        },
        SECURITY_INCIDENTS: '/crm/security-incidents',

        // Analytics & Business Intelligence
        ANALYTICS: {
            DASHBOARD: '/crm/analytics/dashboard',
            RISK_METRICS: '/crm/analytics/risk-metrics',
            BUSINESS_INTELLIGENCE: '/crm/analytics/business-intelligence',
        },

        // Institutional Features
        INSTITUTIONAL: {
            ONBOARDING: '/crm/institutional/onboarding',
            CREDIT_LINES: '/crm/institutional/credit-lines',
            CLIENT_HIERARCHY: '/crm/institutional/client-hierarchy',
        },

        // Compliance
        COMPLIANCE: {
            ALERTS: '/crm/compliance/alerts',
            RULES: '/crm/compliance/rules',
            REPORTS: '/crm/compliance/reports',
            SCORES: '/crm/compliance/scores',
        },

        // Communication & Marketing
        COMMUNICATION: {
            CAMPAIGNS: '/crm/communication/campaigns',
            JOURNEYS: '/crm/communication/journeys',
            NOTIFICATIONS: '/crm/communication/notifications',
            LEAD_SCORING: '/crm/communication/lead-scoring',
            FEEDBACK: '/crm/communication/feedback',
        },

        // Integration & API Management
        INTEGRATION: {
            API_MANAGEMENT: '/crm/integration/api-management',
            WEBHOOKS: '/crm/integration/webhooks',
            DATA_SYNC: '/crm/integration/data-sync',
        },

        // Security & Audit
        SECURITY: {
            MONITORING: '/crm/security/monitoring',
            INCIDENTS: '/crm/security/incidents',
            USER_ACTIVITY: '/crm/security/user-activity',
            DATA_PRIVACY: '/crm/security/data-privacy',
            ACCESS_CONTROL: '/crm/security/access-control',
        },

        // Segments API
        SEGMENTS: {
            LIST: '/crm/segments',
            CREATE: '/crm/segments',
            DETAILS: (segmentId: string) => `/crm/segments/${segmentId}`,
            UPDATE: (segmentId: string) => `/crm/segments/${segmentId}`,
            DELETE: (segmentId: string) => `/crm/segments/${segmentId}`,
            REFRESH_COUNT: (segmentId: string) => `/crm/segments/${segmentId}/refresh-count`,
            ANALYTICS: '/crm/segments/analytics/overview',
        },

        // Templates API
        TEMPLATES: {
            LIST: '/crm/templates',
            CREATE: '/crm/templates',
            DETAILS: (templateId: string) => `/crm/templates/${templateId}`,
            UPDATE: (templateId: string) => `/crm/templates/${templateId}`,
            DELETE: (templateId: string) => `/crm/templates/${templateId}`,
            PREVIEW: (templateId: string) => `/crm/templates/${templateId}/preview`,
            CLONE: (templateId: string) => `/crm/templates/${templateId}/clone`,
            BY_TYPE: (type: string) => `/crm/templates/by-type/${type}`,
            ANALYTICS: '/crm/templates/analytics/overview',
        },

        // Trading Accounts API
        TRADING_ACCOUNTS: {
            LIST: '/crm/trading-accounts',
            DETAILS: (id: string) => `/crm/trading-accounts/${id}`,
            CREATE: '/crm/trading-accounts',
            UPDATE: (id: string) => `/crm/trading-accounts/${id}`,
            DELETE: (id: string) => `/crm/trading-accounts/${id}`,
            RECALCULATE_BALANCE: (id: string) => `/crm/trading-accounts/${id}/recalculate-balance`,
        },

        // Payment Methods API
        PAYMENT_METHODS: {
            LIST: '/crm/payment-methods',
            CREATE: '/crm/payment-methods',
            DETAILS: (paymentMethodId: string) => `/crm/payment-methods/${paymentMethodId}`,
            UPDATE: (paymentMethodId: string) => `/crm/payment-methods/${paymentMethodId}`,
            DELETE: (paymentMethodId: string) => `/crm/payment-methods/${paymentMethodId}`,
            TOGGLE_STATUS: (paymentMethodId: string) =>
                `/crm/payment-methods/${paymentMethodId}/toggle-status`,
            AVAILABLE_CURRENCIES: '/crm/payment-methods/currencies/available',
            STATISTICS: '/crm/payment-methods/statistics/overview',
            PROCESSORS: (paymentMethodId: string) =>
                `/crm/payment-methods/${paymentMethodId}/processors`,
            UPDATE_PROCESSORS: (paymentMethodId: string) =>
                `/crm/payment-methods/${paymentMethodId}/processors`,
            AVAILABLE_PROCESSORS: '/crm/payment-methods/processors/available',
        },

        // Webhooks API
        WEBHOOKS: {
            LIST: '/crm/webhooks',
            CREATE: '/crm/webhooks',
            DETAILS: (webhookId: string) => `/crm/webhooks/${webhookId}`,
            UPDATE: (webhookId: string) => `/crm/webhooks/${webhookId}`,
            DELETE: (webhookId: string) => `/crm/webhooks/${webhookId}`,
            TEST: (webhookId: string) => `/crm/webhooks/${webhookId}/test`,
            RESET_FAILURES: (webhookId: string) => `/crm/webhooks/${webhookId}/reset-failures`,
            DELIVERY_LOGS: (webhookId: string) => `/crm/webhooks/${webhookId}/delivery-logs`,
            ANALYTICS: '/crm/webhooks/analytics/overview',
            AVAILABLE_EVENTS: '/crm/webhooks/events/available',
        },

        // Audit Logs
        AUDIT_LOGS: {
            LIST: '/crm/audit-logs',
            DETAILS: (logId: string) => `/crm/audit-logs/${logId}`,
            EXPORT: '/crm/audit-logs/export',
        },
    },

    // System endpoints
    SYSTEM: {
        CUSTOM_ATTRIBUTE_DEFINITIONS: '/crm/system/custom-attribute-definitions',
        STATUS: '/system-status',
        CONFIGURATION: '/system-configuration',
    },

    // General auth middleware endpoints
    SESSIONS: {
        LIST: '/sessions',
        DELETE: '/sessions',
    },
} as const;
